import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { AuthShell } from './AuthShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, login, isLoading } = useAuthStore();
  const [submitError, setSubmitError] = useState('');
  const locationState = location.state as { from?: { pathname?: string; search?: string } } | null;

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    navigate(user.role === 'admin' ? '/admin' : '/tutor', { replace: true });
  }, [isAuthenticated, navigate, user]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginFormValues) => {
    setSubmitError('');
    try {
      const account = await login(values.email, values.password);
      const requestedPath = locationState?.from?.pathname;
      if (requestedPath && requestedPath.startsWith(account.role === 'admin' ? '/admin' : '/tutor')) {
        navigate(`${requestedPath}${locationState?.from?.search || ''}`, { replace: true });
        return;
      }

      navigate(account.role === 'admin' ? '/admin' : '/tutor', { replace: true });
    } catch (error) {
      setSubmitError((error as Error).message);
    }
  };

  return (
    <AuthShell
      mode="form"
      title="Staff Login"
      subtitle="Only approved admin and tutor accounts can access the academy dashboard."
      eyebrow="Access Control"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Email Address"
          type="email"
          placeholder="name@genkalviacademy.in"
          autoComplete="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email address',
            },
          })}
          error={errors.email?.message}
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          autoComplete="current-password"
          {...register('password', {
            required: 'Password is required',
          })}
          error={errors.password?.message}
        />

        {submitError ? (
          <p className="status-message border-red-100 bg-red-50 text-red-700">{submitError}</p>
        ) : null}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </form>
    </AuthShell>
  );
};
